import { useState } from 'react';
import { formatDateTime } from '@/lib/dateFormat';
import { useAttendanceHistory, useStatuses } from '@/hooks/useAttendance';
import { Button } from '@/components/ui/button';
import { DateRangePresetPicker, useFilterState } from '@/components/filters';
import { StatusSelect } from '@/components/status/StatusSelect';
import { TablePagination } from '@/components/ui/TablePagination';
import { OverbreakBadge, calcOverbreakSeconds } from '@/components/attendance/OverbreakBadge';
import { getSingleStaffAttendanceEndDisplay } from '@/lib/attendanceEndStatus';
import { safeArray } from '@/lib/safeArray';
import { useAuthStore } from '@/stores/auth.store';
import { useSortableTable } from '@/hooks/useSortableTable';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

type AttendanceHistoryFilter = { startDate: string; endDate: string; statusId: string } & Record<string, unknown>;
const DEFAULT_FILTER: AttendanceHistoryFilter = { startDate: '', endDate: '', statusId: '' };

export function AttendanceHistoryPage() {
  const { user } = useAuthStore();
  const isSuperAdmin = user?.roles?.includes('SUPER_ADMIN') ?? false;
  const [page, setPage] = useState(1);
  const [filter, setFilter, resetFilter] = useFilterState<AttendanceHistoryFilter>({
    key: 'vibe365.attendance-history.filter',
    defaultValue: DEFAULT_FILTER,
    mode: 'localStorage',
  });

  const { data: statusesData = [] } = useStatuses();
  const { data, isLoading } = useAttendanceHistory({
    page,
    limit: 20,
    startDate: filter.startDate || undefined,
    endDate: filter.endDate || undefined,
    statusId: filter.statusId || undefined,
    clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const records = safeArray(data?.data);
  const pagination = data?.pagination;

  const { sorted: sortedRecords, sortState, toggleSort } = useSortableTable(records, {
    status: (r) => r.status.displayName ?? r.status.name,
    start: (r) => r.startTime,
    end: (r) => r.endTime ?? '',
    duration: (r) => r.durationSeconds ?? -1,
    notes: (r) => r.notes ?? '',
    createdAt: (r) => r.logCreatedAt,
  });

  function SortIcon({ col }: { col: string }) {
    if (sortState.key !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-1 opacity-40" />;
    return sortState.dir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-1" />
      : <ChevronDown className="inline h-3 w-3 ml-1" />;
  }

  function updateFilter(patch: Partial<AttendanceHistoryFilter>) {
    setFilter((f) => ({ ...f, ...patch }));
    setPage(1);
  }

  const handleReset = () => {
    resetFilter();
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Attendance History</h1>
        <p className="text-sm text-muted-foreground">View your attendance records</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-3 rounded-xl border bg-card p-4 shadow-sm">
        <DateRangePresetPicker
          value={{ startDate: filter.startDate, endDate: filter.endDate }}
          onChange={({ startDate: nextStartDate, endDate: nextEndDate }) =>
            updateFilter({ startDate: nextStartDate ?? '', endDate: nextEndDate ?? '' })
          }
          className="w-full sm:w-[20rem]"
        />
        <StatusSelect
          label="Status"
          value={filter.statusId}
          options={statusesData}
          placeholder="Choose a status to filter"
          emptyLabel="All statuses"
          onValueChange={(nextValue) => updateFilter({ statusId: nextValue })}
          className="w-full sm:w-[18rem]"
          triggerClassName="w-full"
          testId="attendance-history-status-filter"
        />
        <Button variant="outline" onClick={handleReset} className="h-10">Reset</Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-card shadow-sm">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : records.length === 0 ? (
            <div className="flex h-32 items-center justify-center">
              <p className="text-sm text-muted-foreground">No records found</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
                  <th className="px-6 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort('status')}>Status<SortIcon col="status" /></th>
                  <th className="px-6 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort('start')}>Start<SortIcon col="start" /></th>
                  <th className="px-6 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort('end')}>End<SortIcon col="end" /></th>
                  <th className="px-6 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort('duration')}>Duration<SortIcon col="duration" /></th>
                  <th className="px-6 py-3 font-medium">Overbreak</th>
                  <th className="px-6 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort('notes')}>Notes<SortIcon col="notes" /></th>
                  {isSuperAdmin && <th className="px-6 py-3 font-medium">Debug Info</th>}
                  <th className="px-6 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort('createdAt')}>Created At<SortIcon col="createdAt" /></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sortedRecords.map((record, index) => {
                  const endDisplay = getSingleStaffAttendanceEndDisplay(records, index);

                  return <tr key={record.id} className="hover:bg-muted/30">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: record.status.colorHex ?? '#6b7280' }}
                        />
                        {record.status.displayName ?? record.status.name}
                      </div>
                    </td>
                    <td className="px-6 py-3 font-mono text-xs">
                      {formatDateTime(record.startTime)}
                    </td>
                    <td className="px-6 py-3 font-mono text-xs">
                      {record.endTime
                        ? formatDateTime(record.endTime)
                        : <span className={endDisplay?.className}>{endDisplay?.label}</span>}
                    </td>
                    <td className="px-6 py-3 font-mono text-xs">
                      {formatDuration(record.durationSeconds)}
                    </td>
                    <td className="px-6 py-3">
                      <OverbreakBadge
                        overbreakSeconds={calcOverbreakSeconds(
                          record.durationSeconds,
                          record.status.isBreak,
                          record.status.maxDurationSeconds,
                        )}
                      />
                    </td>
                    <td className="px-6 py-3 text-xs text-muted-foreground">
                      {record.notes ?? '—'}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-6 py-3 font-mono text-xs text-muted-foreground">
                        {record.debugInfo ?? '—'}
                      </td>
                    )}
                    <td className="px-6 py-3 font-mono text-xs text-muted-foreground">
                      {formatDateTime(record.logCreatedAt)}
                    </td>
                  </tr>;
                })}
              </tbody>
            </table>
          )}
        </div>

        {pagination && (
          <TablePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}
